import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, ArrowLeft, Upload, Check, Gift, Loader2, X, Image, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LICENSE_TYPES = [
  { value: 'cosmetology', label: 'Cosmetology (Hair)', description: 'Hair stylists, colorists' },
  { value: 'barber', label: 'Barber', description: 'Barbers, fades, mens grooming' },
  { value: 'nails', label: 'Nail Technician', description: 'Manicurists, pedicurists, nail artists' },
  { value: 'esthetician', label: 'Esthetician', description: 'Skincare, facials, waxing' }
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const licenseInputRef = useRef(null);
  const idInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    salon_name: '',
    phone: '',
    license_type: 'cosmetology',
    license_number: '',
    license_expiration: '',
    license_url: '',
    id_url: '',
    referral_code: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Upload states for license
  const [uploadingLicense, setUploadingLicense] = useState(false);
  const [licensePreview, setLicensePreview] = useState(null);
  
  // Upload states for ID
  const [uploadingId, setUploadingId] = useState(false);
  const [idPreview, setIdPreview] = useState(null);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      toast.success('Referral code applied!');
    }
  }, [searchParams]);

  const uploadImage = async (file, folder) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await axios.post(`${API}/uploads/photo?folder=${encodeURIComponent(folder)}`, form, {
        timeout: 120000
      });
      return response.data.secure_url;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Upload failed. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleLicenseSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) {
      toast.error('Please upload an image file (JPG, PNG, etc.)');
      return;
    }

    // Keep the browser limit consistent with the server.
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File too large (${formatFileSize(file.size)}). Maximum size is 10MB.`);
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setLicensePreview(e.target.result);
    reader.readAsDataURL(file);

    setUploadingLicense(true);
    toast.info(`Uploading ${formatFileSize(file.size)}...`);
    
    try {
      const url = await uploadImage(file, 'licenses');
      setFormData(prev => ({ ...prev, license_url: url }));
      toast.success('Professional license uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message || 'Please try again or use a smaller image.'}`);
      setLicensePreview(null);
      setFormData(prev => ({ ...prev, license_url: '' }));
      if (licenseInputRef.current) licenseInputRef.current.value = '';
    } finally {
      setUploadingLicense(false);
    }
  };

  const handleIdSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) {
      toast.error('Please upload an image file (JPG, PNG, etc.)');
      return;
    }

    // Keep the browser limit consistent with the server.
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File too large (${formatFileSize(file.size)}). Maximum size is 10MB.`);
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setIdPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploadingId(true);
    toast.info(`Uploading ${formatFileSize(file.size)}...`);
    
    try {
      const url = await uploadImage(file, 'licenses');
      setFormData(prev => ({ ...prev, id_url: url }));
      toast.success('ID uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message || 'Please try again or use a smaller image.'}`);
      setIdPreview(null);
      setFormData(prev => ({ ...prev, id_url: '' }));
      if (idInputRef.current) idInputRef.current.value = '';
    } finally {
      setUploadingId(false);
    }
  };

  const handleRemoveLicense = () => {
    setLicensePreview(null);
    setFormData(prev => ({ ...prev, license_url: '' }));
    if (licenseInputRef.current) licenseInputRef.current.value = '';
  };

  const handleRemoveId = () => {
    setIdPreview(null);
    setFormData(prev => ({ ...prev, id_url: '' }));
    if (idInputRef.current) idInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!formData.license_number) {
      toast.error('Please enter your license number');
      return;
    }

    if (!formData.license_expiration) {
      toast.error('Please enter your license expiration date');
      return;
    }

    if (!formData.license_url) {
      toast.error('Please upload your professional license');
      return;
    }

    if (!formData.id_url) {
      toast.error('Please upload your ID/driver\'s license');
      return;
    }

    if (!agreedToTerms) {
      toast.error('You must agree to the Terms of Use to create an account');
      return;
    }

    setLoading(true);
    
    try {
      const { confirmPassword, ...userData } = formData;
      await register(userData);
      toast.success('Account created! Your credentials are pending admin approval.');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedLicense = LICENSE_TYPES.find(l => l.value === formData.license_type);

  const renderUploadBox = (type, preview, uploading, inputRef, onSelect, onRemove, icon, title, subtitle) => {
    const Icon = icon;
    
    return (
      <div className="space-y-2">
        <Label className="text-stone-700">{title} *</Label>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onSelect}
          className="hidden"
          data-testid={`${type}-file-input`}
        />
        
        {preview ? (
          <div className="relative border-2 border-[#5F8D4E] rounded-lg p-2 bg-[#5F8D4E]/5">
            <img 
              src={preview} 
              alt={`${title} preview`} 
              className="w-full h-32 object-contain rounded"
            />
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              data-testid={`remove-${type}-btn`}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center justify-center gap-2 text-[#5F8D4E] mt-2">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Uploaded</span>
            </div>
          </div>
        ) : (
          <div 
            onClick={() => !uploading && inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              uploading 
                ? 'border-stone-300 bg-stone-50' 
                : 'border-stone-300 hover:border-[#D97757] hover:bg-[#D97757]/5'
            }`}
            data-testid={`${type}-upload`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-stone-500">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-stone-500">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">{subtitle}</span>
                <span className="text-xs text-stone-400">PNG, JPG up to 10MB</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg" 
          alt="Beauty Professional" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-white text-3xl font-heading mb-4">
            Join 500+ Beauty Professionals
          </h2>
          <p className="text-stone-300 text-lg">
            Hair stylists, barbers, nail techs, and estheticians share client reviews to protect their businesses.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <Card className="border-stone-200 shadow-xl" data-testid="register-card">
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-[#D97757] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              </div>
              <CardTitle className="font-heading text-2xl text-stone-900">Create Account</CardTitle>
              <CardDescription className="text-stone-600">Join the professional community</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-stone-700">Full Name *</Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="Jane Smith"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                    data-testid="register-fullname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-stone-700">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jane@salon.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salon_name" className="text-stone-700">Business Name</Label>
                    <Input
                      id="salon_name"
                      type="text"
                      placeholder="Studio 54"
                      value={formData.salon_name}
                      onChange={(e) => setFormData({ ...formData, salon_name: e.target.value })}
                      className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                      data-testid="register-salon-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-stone-700">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="555-0123"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>

                {/* License Type Selection */}
                <div className="space-y-2">
                  <Label className="text-stone-700">License Type *</Label>
                  <Select 
                    value={formData.license_type} 
                    onValueChange={(value) => setFormData({ ...formData, license_type: value })}
                  >
                    <SelectTrigger className="h-11 rounded-lg border-stone-200" data-testid="license-type-select">
                      <SelectValue placeholder="Select your license type" />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_TYPES.map((license) => (
                        <SelectItem key={license.value} value={license.value}>
                          <div className="flex flex-col">
                            <span>{license.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedLicense && (
                    <p className="text-xs text-stone-500">{selectedLicense.description}</p>
                  )}
                </div>

                {/* License Information Section */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-center mb-2">
                    <p className="text-sm font-medium text-stone-700">License Information</p>
                    <p className="text-xs text-stone-500">We&apos;ll notify you 60 days before expiration</p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="license_number" className="text-stone-700">License Number *</Label>
                      <Input
                        id="license_number"
                        value={formData.license_number}
                        onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                        placeholder="e.g., COS-123456"
                        className="h-11 rounded-lg border-stone-200"
                        data-testid="license-number-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="license_expiration" className="text-stone-700">Expiration Date *</Label>
                      <Input
                        id="license_expiration"
                        type="date"
                        value={formData.license_expiration}
                        onChange={(e) => setFormData({ ...formData, license_expiration: e.target.value })}
                        className="h-11 rounded-lg border-stone-200"
                        data-testid="license-expiration-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Document Uploads Section */}
                <div className="space-y-4 p-4 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="text-center mb-2">
                    <p className="text-sm font-medium text-stone-700">Upload Required Documents</p>
                    <p className="text-xs text-stone-500">We verify your identity matches your license</p>
                  </div>
                  
                  {/* Professional License Upload */}
                  {renderUploadBox(
                    'license',
                    licensePreview,
                    uploadingLicense,
                    licenseInputRef,
                    handleLicenseSelect,
                    handleRemoveLicense,
                    Image,
                    'Professional License',
                    'Upload your professional license'
                  )}

                  {/* ID/Driver's License Upload */}
                  {renderUploadBox(
                    'id',
                    idPreview,
                    uploadingId,
                    idInputRef,
                    handleIdSelect,
                    handleRemoveId,
                    CreditCard,
                    "Driver's License / ID",
                    'Upload your ID for verification'
                  )}
                </div>

                {/* Referral Code */}
                <div className="space-y-2">
                  <Label htmlFor="referral_code" className="text-stone-700 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-[#5F8D4E]" />
                    Referral Code (Optional)
                  </Label>
                  <Input
                    id="referral_code"
                    type="text"
                    placeholder="e.g. SR1A2B3C"
                    value={formData.referral_code}
                    onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                    className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757] uppercase"
                    data-testid="register-referral-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-stone-700">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757] pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-stone-700">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="h-11 rounded-lg border-stone-200 focus:border-[#D97757] focus:ring-[#D97757]"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                {/* Terms of Use Agreement */}
                <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-stone-300 text-[#D97757] focus:ring-[#D97757]"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-agreement" className="text-sm text-stone-700 leading-relaxed">
                      I have read, understood, and agree to the{' '}
                      <Link 
                        to="/terms" 
                        target="_blank"
                        className="text-[#D97757] hover:underline font-medium"
                      >
                        Terms of Use
                      </Link>
                      , including the Client Review Guidelines and Indemnification provisions.
                    </label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || uploadingLicense || uploadingId || !agreedToTerms}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-stone-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#D97757] hover:underline font-medium" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
